/**
 * Custom hook for drift analysis with SSE streaming
 */

import { useState, useCallback, useRef } from 'react';
import { scanRepository, scanPullRequest, analyzeDriftStream } from '../services/api';

export function useDriftAnalysis() {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [currentNode, setCurrentNode] = useState(null);
  const controllerRef = useRef(null);

  const addProgress = useCallback((message, type = 'info') => {
    setProgress(prev => [...prev, { message, type, timestamp: new Date().toISOString() }]);
  }, []);

  const handleEvent = useCallback((eventType, data) => {
    console.log('SSE Event:', eventType, data);

    switch (eventType) {
      case 'resolving':
        addProgress(data.step, 'info');
        if (data.base_ref && data.head_ref) {
          addProgress(`Comparing ${data.base_ref} → ${data.head_ref}`, 'success');
        }
        break;

      case 'pipeline_start':
        addProgress('Starting drift detection pipeline...', 'info');
        setCurrentNode('starting');
        break;

      case 'node_start':
        setCurrentNode(data.node);
        addProgress(`Running: ${formatNodeName(data.node)}`, 'info');
        break;

      case 'node_complete':
        addProgress(
          `✓ ${formatNodeName(data.node)} completed in ${data.duration_ms}ms`,
          'success'
        );
        break;

      case 'drift_found':
        addProgress(
          `Found drift in ${data.file} (${data.severity})`,
          data.severity === 'CRITICAL' || data.severity === 'HIGH' ? 'warning' : 'info'
        );
        break;

      case 'analysis_complete':
        addProgress(
          `Analysis complete! Health score: ${data.health_score}/100`,
          data.health_score >= 80 ? 'success' : data.health_score >= 60 ? 'warning' : 'error'
        );
        setCurrentNode(null);
        break;

      case 'result':
        setResult(data);
        setIsAnalyzing(false);
        setCurrentNode(null);
        addProgress('✓ Results ready', 'success');
        break;

      case 'error':
        setError(data.error_message || 'Analysis failed');
        setIsAnalyzing(false);
        setCurrentNode(null);
        addProgress(`✗ Error: ${data.error_message}`, 'error');
        break;

      default:
        console.log('Unknown event:', eventType, data);
    }
  }, [addProgress]);

  const startScan = useCallback(async (repoUrl, githubToken = null) => {
    setIsAnalyzing(true);
    setProgress([]);
    setResult(null);
    setError(null);
    setCurrentNode(null);

    try {
      addProgress('Initializing scan...', 'info');
      controllerRef.current = await scanRepository(repoUrl, githubToken, handleEvent);
    } catch (err) {
      setError(err.message);
      setIsAnalyzing(false);
      addProgress(`✗ Failed to start scan: ${err.message}`, 'error');
    }
  }, [handleEvent, addProgress]);

  const startPRScan = useCallback(async (repoUrl, prNumber = null, githubToken = null) => {
    setIsAnalyzing(true);
    setProgress([]);
    setResult(null);
    setError(null);
    setCurrentNode(null);

    try {
      addProgress('Initializing PR scan...', 'info');
      controllerRef.current = await scanPullRequest(repoUrl, prNumber, githubToken, handleEvent);
    } catch (err) {
      setError(err.message);
      setIsAnalyzing(false);
      addProgress(`✗ Failed to start PR scan: ${err.message}`, 'error');
    }
  }, [handleEvent, addProgress]);

  const startManualAnalysis = useCallback(async (repoUrl, baseRef, headRef, githubToken = null) => {
    setIsAnalyzing(true);
    setProgress([]);
    setResult(null);
    setError(null);
    setCurrentNode(null);

    try {
      addProgress('Initializing analysis...', 'info');
      controllerRef.current = await analyzeDriftStream(
        repoUrl,
        baseRef,
        headRef,
        githubToken,
        handleEvent
      );
    } catch (err) {
      setError(err.message);
      setIsAnalyzing(false);
      addProgress(`✗ Failed to start analysis: ${err.message}`, 'error');
    }
  }, [handleEvent, addProgress]);

  const cancelAnalysis = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.cancel();
      controllerRef.current = null;
    }
    setIsAnalyzing(false);
    setCurrentNode(null);
    addProgress('Analysis cancelled', 'warning');
  }, [addProgress]);

  const reset = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.cancel();
      controllerRef.current = null;
    }
    setIsAnalyzing(false);
    setProgress([]);
    setResult(null);
    setError(null);
    setCurrentNode(null);
  }, []);

  return {
    isAnalyzing,
    progress,
    result,
    error,
    currentNode,
    startScan,
    startPRScan,
    startManualAnalysis,
    cancelAnalysis,
    reset,
  };
}

function formatNodeName(node) {
  const names = {
    fetch_changes: 'Fetching code changes',
    fetch_docs: 'Fetching documentation',
    analyze_drift: 'Analyzing drift with AI',
    calculate_health: 'Calculating health score',
  };
  return names[node] || node;
}

// Made with Bob
